/**
 * Sawyer 03 today-notes-board (G8 / C2b) — the desk's same-day note, written
 * from the Front Desk row at drop-off ("parents want this specific for
 * today", memo 2, 30:10).
 *
 * No new storage and no new route: the note is `booking_dogs.run_card_edits
 * .note` (#1218), the stay's note that already prints on the run card, saved
 * through the existing PUT with `{ note }` only. '' clears it (the route
 * normalises '' to NULL). One labelled textarea per dog; only the dogs whose
 * trimmed text changed are PUT, one after another, so a partial failure can
 * say exactly which dog saved (refuter AC-N5) and a retry re-sends only what
 * is still unsaved.
 *
 * States (DESIGN-PHILOSOPHY): default, focus, busy ("Saving…", disabled),
 * error (inline, text kept, Save re-enabled), and success (closes; the page
 * refetches /today so the row shows what the server stored, one source).
 */
import { useState } from 'react';
import { Alert, Modal } from '@ferro/ui';
import { ApiError, saveRunCardEdits } from '../../lib/api.js';
import './booking-notes.css';

export const DESK_NOTE_MAX = 1000;
const DESK_NOTE_NEAR = 900;

export interface DeskNoteDialogProps {
  readonly apiUrl: string;
  readonly token: string;
  readonly bookingId: string;
  readonly dogs: ReadonlyArray<{ dogId: string; name: string }>;
  readonly current: ReadonlyArray<{ dogId: string; note: string }>;
  /** Something was saved; the page refetches /today. */
  readonly onSaved: () => void;
  readonly onClose: () => void;
}

type Failure = { kind: 'gone' } | { kind: 'failed' };

/** The copy for a failed save, in the desk's words (AC11 c/d, AC-N5). */
export function deskNoteErrorCopy(
  failure: Failure,
  savedNames: ReadonlyArray<string>,
  failedName: string | null,
): string {
  if (failure.kind === 'gone') return 'This visit is no longer on today\'s list. Refresh Front Desk.';
  if (savedNames.length > 0 && failedName !== null) {
    const saved = savedNames.map((n) => `${n}'s`).join(' and ');
    return `Saved ${saved} note. Couldn't save ${failedName}'s note. Your text is still here — try again.`;
  }
  return "Couldn't save the note. Your text is still here — try again.";
}

function failureOf(err: unknown): Failure {
  return err instanceof ApiError && err.status === 404 ? { kind: 'gone' } : { kind: 'failed' };
}

export function DeskNoteDialog({
  apiUrl,
  token,
  bookingId,
  dogs,
  current,
  onSaved,
  onClose,
}: DeskNoteDialogProps): JSX.Element {
  const initial = (dogId: string) => current.find((c) => c.dogId === dogId)?.note ?? '';
  // What the server holds for each dog, as far as this dialog knows. It moves
  // forward on each successful PUT, so a retry only re-sends unsaved dogs.
  const [stored, setStored] = useState<Record<string, string>>(() =>
    Object.fromEntries(dogs.map((d) => [d.dogId, initial(d.dogId).trim()])),
  );
  const [text, setText] = useState<Record<string, string>>(() =>
    Object.fromEntries(dogs.map((d) => [d.dogId, initial(d.dogId)])),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAny, setSavedAny] = useState(false);

  const close = (): void => {
    if (busy) return;
    if (savedAny) onSaved();
    onClose();
  };

  const save = async (): Promise<void> => {
    const changed = dogs.filter((d) => (text[d.dogId] ?? '').trim() !== (stored[d.dogId] ?? ''));
    if (changed.length === 0) {
      onClose();
      return;
    }
    setBusy(true);
    setError(null);
    const savedNames: string[] = [];
    let nextStored = stored;
    for (const dog of changed) {
      const note = (text[dog.dogId] ?? '').trim();
      try {
        await saveRunCardEdits(apiUrl, token, bookingId, dog.dogId, { note: note.length > 0 ? note : null });
        nextStored = { ...nextStored, [dog.dogId]: note };
        savedNames.push(dog.name);
      } catch (err) {
        setStored(nextStored);
        if (savedNames.length > 0) setSavedAny(true);
        setError(deskNoteErrorCopy(failureOf(err), savedNames, dogs.length > 1 ? dog.name : null));
        setBusy(false);
        return;
      }
    }
    setBusy(false);
    onSaved();
    onClose();
  };

  return (
    <Modal
      isOpen
      onClose={close}
      title="Note for this visit"
      size="md"
      className="ferro-desk-note"
      footer={
        <div className="ferro-desk-note__actions">
          <button
            type="button"
            className="ferro-btn ferro-btn--ghost"
            onClick={close}
            disabled={busy}
            data-testid="desk-note-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            className="ferro-btn ferro-btn--primary"
            onClick={() => void save()}
            disabled={busy}
            data-testid="desk-note-save"
          >
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      }
    >
      <div data-testid="desk-note-dialog">
        <p className="ferro-muted ferro-desk-note__helper">
          Shows on Front Desk, the Dashboard, the morning board and the run card.
        </p>
        {error ? (
          <Alert variant="error">
            <span data-testid="desk-note-error">{error}</span>
          </Alert>
        ) : null}
        {dogs.map((dog) => {
          const value = text[dog.dogId] ?? '';
          const near = value.length > DESK_NOTE_NEAR;
          const id = `desk-note-${bookingId}-${dog.dogId}`;
          return (
            <div className="ferro-desk-note__field" key={dog.dogId}>
              <label htmlFor={id}>{dog.name}</label>
              <textarea
                id={id}
                className="ferro-desk-note__textarea"
                value={value}
                maxLength={DESK_NOTE_MAX}
                rows={3}
                disabled={busy}
                onChange={(e) => setText((t) => ({ ...t, [dog.dogId]: e.target.value }))}
                data-testid={`desk-note-textarea-${dog.dogId}`}
              />
              <p
                className={`ferro-muted ferro-desk-note__counter${near ? ' ferro-desk-note__counter--near' : ''}`}
                data-testid={`desk-note-counter-${dog.dogId}`}
                aria-live="polite"
              >
                {value.length}/{DESK_NOTE_MAX}
                {near ? ` · ${DESK_NOTE_MAX - value.length} characters left` : ''}
              </p>
            </div>
          );
        })}
      </div>
    </Modal>
  );
}
